import { KPI_SNAPSHOT, BRANCHES, SUPPLIERS } from "./data";
import { PILOT_ALERTS, demandOutlookFor, branchHealth } from "./dataPilot";
import { combineOrigins, originFromDataMode, fmtDate } from "./origin.jsx";
import { formatPeso, formatNumber, pct } from "./utils";

const NAV = [
  ["/morning-brief", "Morning Brief"], ["/", "Command Center"], ["/inventory", "Inventory Intelligence"],
  ["/procurement", "Procurement & Supply"], ["/forecast", "Forecast / Prediction Models"], ["/branches", "Branch Performance"],
  ["/operations", "Daily Operations"], ["/anomalies", "Anomaly Detection"], ["/twin", "Operational Digital Twin"],
  ["/workbench", "Simulation Workbench"], ["/kpi", "KPI Framework"], ["/reports", "Reports Center"],
];

// Compact, section-tagged inventory & demand snapshot that grounds the Copilot.
// Each section carries its data origin so the reply can be labelled from the sections it cites.
export function buildDashboardSnapshot({ valuation, recs = [], canAccess = () => true, branchId, extra = [] } = {}) {
  const sections = [];
  const add = (label, origin, lines) => sections.push({ id: `S${sections.length + 1}`, label, origin, lines });
  const branches = branchId ? BRANCHES.filter(b => b.id === branchId) : BRANCHES;
  const demand = demandOutlookFor(branchId);

  add("Inventory & demand KPIs", "synthetic", [
    `Inventory turnover ${KPI_SNAPSHOT.inventoryTurnover.toFixed(1)}x (target 8-12x) · Days on hand ${KPI_SNAPSHOT.daysSalesInventory}d`,
    `Stockout SKUs ${KPI_SNAPSHOT.stockoutSKUs} · Overstock SKUs ${KPI_SNAPSHOT.overstockSKUs}`,
    `Forecast accuracy (A-class) ${pct(KPI_SNAPSHOT.forecastAccuracy)} · Supplier on-time ${pct(KPI_SNAPSHOT.supplierOTD)}`,
    `Demand (${demand.scope}) last 30d ${formatNumber(demand.last30)} units · forecast next 30d ${formatNumber(demand.next30)} units`,
  ]);
  if (valuation) {
    add("Ending Inventory valuation (historical cost)", valuation.origin, [
      `Total ${formatPeso(valuation.total)} as of ${fmtDate(valuation.asOf)}${valuation.stale ? ` (stale, ${valuation.ageDays}d old)` : ""}`,
      `${formatNumber(valuation.itemsValued)} items · ${formatNumber(Math.round(valuation.unitsValued))} units valued · ${formatNumber(valuation.excluded)} items excluded (no recorded cost)`,
      ...valuation.byCategory.slice(0, 4).map(c => `${c.name}: ${formatPeso(c.value, { compact: true })} (${(c.share * 100).toFixed(1)}%)`),
      ...valuation.byBranch.slice(0, 5).map(b => `${b.name} (${b.region}): ${formatPeso(b.value, { compact: true })}`),
    ]);
  }
  if (recs.length) {
    add("Seven-engine top recommendations", combineOrigins(recs.map(r => originFromDataMode(r.data_mode))),
      recs.slice(0, 5).map(r => `${r.engine_label || r.engine}: ${r.title} — ${formatPeso(r.expected_value_php || 0, { compact: true })} expected (${r.scope?.branch_name || "network"})`));
  }
  const atRisk = branches.filter(b => branchHealth(b) === "at_risk").sort((a, b) => b.stockoutRate - a.stockoutRate);
  add("Branch stock health", "synthetic", [
    `${atRisk.length} of ${branches.length} branches at stockout risk`,
    ...atRisk.slice(0, 5).map(b => `${b.name} (${b.region}): stockout ${(b.stockoutRate * 100).toFixed(1)}%, ${b.daysOnHand}d on hand`),
  ]);
  const risky = SUPPLIERS.filter(s => s.otd < 0.85);
  add("Suppliers needing escalation (OTD < 85%)", "synthetic",
    risky.length ? risky.map(s => `${s.name} (${s.category}): OTD ${(s.otd * 100).toFixed(0)}%, lead time ${s.leadTime}d, ${s.openPOs} open POs`) : ["none"]);
  add("Inventory, demand & supply alerts", "synthetic",
    PILOT_ALERTS.filter(a => a.severity !== "info").slice(0, 6).map(a => a.text));

  extra.forEach(e => add(e.label, e.origin, e.lines));
  const body = sections.map(s => `### [${s.id}] ${s.label.toUpperCase()} — origin: ${s.origin}\n${s.lines.map(l => `- ${l}`).join("\n")}`).join("\n\n");
  const nav = NAV.filter(([p]) => canAccess(p)).map(([, l]) => `- ${l}`).join("\n");
  return {
    text: `${body}\n\n### NAVIGATION (modules this user can open — refer only to these)\n${nav}`,
    sections: sections.map(({ id, label, origin }) => ({ id, label, origin })),
  };
}
